import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    AccessTeamDTO,
    useDeleteAccessTeamApi,
    useSaveAccessTeamApi,
    useSearchAccessTeamApi,
} from '../../../api/access-team-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { CreatedRowInfo } from '../../../components/table/created-row-info';
import { ToggleCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveAccessTeamModal } from './save-access-team-modal';

export function AccessTeamApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedAccessTeam, setSelectedAccessTeam] = useState<AccessTeamDTO | undefined>();
    const accessTeamInit = useSearchAccessTeamApi();
    const saveAccessTeam = useSaveAccessTeamApi();
    const deleteAccessTeam = useDeleteAccessTeamApi();

    const { result, error, busy, initialized, initializing } = accessTeamInit;

    const userTitle = useCallback(
        (userName?: string) => {
            var user = result?.users.find(x => x.userName === userName);
            return <span>{user ? `${user.firstName} ${user.lastName} ` : userName}</span>;
        },
        [result?.users]
    );

    const columns: IBasicTableColumn<AccessTeamDTO>[] = useMemo(
        () => [
            {
                Header: <T>serial</T>,
                name: 'serial',
                width: 80,
                Cell: ({ row }) => <N>{row.serial}</N>,
            },
            {
                Header: <T>title</T>,
                name: 'title',
                width: 80,
            },
            {
                Header: <T>description</T>,
                name: 'description',
                width: 80,
            },
            {
                Header: <T>is-active</T>,
                name: 'isActive',
                width: 80,
                Cell: ToggleCell,
            },

            {
                Header: <T>created-by</T>,
                name: 'createdBy',
                width: 100,
                Cell: CreatedRowInfo,
            },
        ],
        [userTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        accessTeamInit.post(null, {});
    }, [initialized, initializing, error, accessTeamInit]);

    const handleDelete = (accessTeam: AccessTeamDTO) => {
        bd.msgbox(
            translate('delete-access-team'),
            translate('confirm-delete-access-team'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteAccessTeam.remove(
                            accessTeam,
                            () => {
                                accessTeamInit.updateResult({
                                    ...result,
                                    accessTeams: result.accessTeams.filter(
                                        b => b.serial !== accessTeam.serial
                                    ),
                                });
                                notify.dark(translate('selected-access-team-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedAccessTeam(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (accessTeam: AccessTeamDTO) => {
        saveAccessTeam.post(
            null,
            accessTeam,
            res => {
                if (selectedAccessTeam) {
                    accessTeamInit.updateResult({
                        ...result,
                        accessTeams: result.accessTeams.map(b =>
                            b.serial === accessTeam.serial ? res.accessTeam : b
                        ),
                    });
                } else {
                    accessTeamInit.updateResult({
                        ...result,
                        accessTeams: [res.accessTeam, ...result.accessTeams],
                    });
                }

                setShowModal(false);
                setSelectedAccessTeam(undefined);
                notify.success(
                    selectedAccessTeam
                        ? translate('selected-access-team-updated-successfully')
                        : translate('new-access-team-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => accessTeamInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="access-team"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-access-team</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedAccessTeam(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.accessTeams}
                        onRowClick={({ row }) => {
                            setSelectedAccessTeam(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveAccessTeamModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedAccessTeam(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedAccessTeam={selectedAccessTeam}
                        users={result?.users}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
